/*
  Shrine: gift options in the basket (drawer and basket page).
  Markup: snippets/shrine-gift-options.liquid. Bottle count and gift box variants: sections/shrine-gift-bottle-count.liquid.

  THE RULE: the panel never changes the basket or the gift details on its own. The only changes are direct customer actions:
    - clicking "Add"                         adds one gift box (the size picked in the dropdown) to the basket
    - ticking / unticking "This is a gift"   sets / clears the cart attributes Gift and Gift message (never touches box lines)
    - typing the message                     saves the cart attribute Gift message
  The panel never reads which gift boxes are already in the basket, and never removes, swaps or corrects one. Customers
  manage boxes with the normal basket quantity selector. Nothing is cleared when the basket is emptied.

  The box row: shown when "This is a gift" is ticked and there is at least 1 qualifying bottle (counted on the server).
  The dropdown lists every size. It starts on the smallest size that holds the bottle count, capped at the largest size
  (1 -> 1, 2 -> 2, 3 or more -> 3); a sold-out size is shown but disabled, and if the starting size is sold out the nearest
  available one is picked. The same rule is written in Liquid in snippets/shrine-gift-options.liquid for the first paint:
  keep the two in step.
*/
(function () {
  'use strict';

  var SAVE_DELAY_MS = 600;           // wait this long after typing stops before saving the message
  var ADDED_FLASH_MS = 2000;         // how long the button says "Added" after a box is added
  var GENERIC_ERROR = 'Sorry, something went wrong. Please try again.';

  var errorText = '';       // message from a failed request
  var last = null;          // last state read from the cart
  var queue = Promise.resolve();
  var syncQueued = false;
  var saveTimers = new WeakMap();   // textarea -> timer
  var dirty = new WeakSet();        // textareas with typing not yet saved
  var saving = 0;                   // message saves in flight
  var lastSave = Promise.resolve(); // the most recent message save (saves run one at a time, so this is the last to finish)
  var allowRetry = false;           // after a failed save at checkout, the next click goes through
  var busy = false;                 // an "Add" request is running: the button stays disabled so a double click can't add two boxes
  var flash = false;                // the button is showing "Added" for a moment
  var passNext = null;              // the form we are re-submitting ourselves after waiting for a save

  /* ---------- requests ---------- */

  function url(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + path;
  }

  function getJSON(path) {
    return fetch(path, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      });
  }

  function post(path, body) {
    return fetch(url(path), {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,   // lets a save finish even if the customer heads straight to an express checkout button
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (json) {
        if (!r.ok) throw new Error(json.description || json.message || GENERIC_ERROR);
        return json;
      });
    });
  }

  // Run our requests one at a time so quick clicks cannot cross over.
  function enqueue(fn) {
    var run = queue.then(fn);
    queue = run.catch(function () {});
    return run;
  }

  /* ---------- reading the cart ---------- */

  // The first number in a variant's title is how many bottles it holds ("2 bottles" -> 2).
  // No number found = 0 = never offered (safe fallback). Same rule as snippets/shrine-gift-capacity.liquid.
  function parseCapacity(title) {
    var m = String(title || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  }

  function fetchState() {
    var t = Date.now();
    return Promise.all([
      getJSON(url('cart.js?t=' + t)),
      getJSON(url('?sections=shrine-gift-bottle-count&t=' + t))
    ]).then(function (res) {
      var doc = new DOMParser().parseFromString(res[1]['shrine-gift-bottle-count'] || '', 'text/html');
      var countEl = doc.querySelector('[data-shrine-gift-count]');
      var variants = [];
      try {
        variants = JSON.parse(doc.querySelector('[data-shrine-gift-variants]').textContent);
      } catch (e) { variants = []; }
      variants = variants.map(function (v) {
        return {
          id: v.id, title: v.title, productTitle: v.productTitle, price: v.price, available: v.available,
          image: v.image, image2x: v.image2x, capacity: parseCapacity(v.title)
        };
      }).filter(function (v) { return v.capacity > 0; });

      return {
        cart: res[0],
        bottles: countEl ? parseInt(countEl.dataset.bottles, 10) || 0 : 0,
        variants: variants
      };
    });
  }

  function variantById(s, id) {
    return s.variants.filter(function (v) { return v.id === id; })[0] || null;
  }

  // Which size the dropdown starts on (see the rule at the top). Returns { variant, soldOut } or null (no row).
  //   soldOut = true means every size is sold out, so "variant" is just the one we would have suggested.
  function defaultVariant(s) {
    if (s.bottles < 1 || !s.variants.length) return null;
    var target = null;
    var largest = null;
    s.variants.forEach(function (v) {
      if (!largest || v.capacity > largest.capacity) largest = v;
      if (v.capacity >= s.bottles && (!target || v.capacity < target.capacity)) target = v;
    });
    target = target || largest;   // more bottles than the biggest box: suggest the biggest
    if (target.available) return { variant: target, soldOut: false };

    // Suggested size is sold out: take the nearest size that is available (a tie goes to the bigger box).
    var best = null;
    var bestDistance = Infinity;
    s.variants.forEach(function (v) {
      if (!v.available) return;
      var distance = Math.abs(v.capacity - target.capacity);
      if (distance < bestDistance || (distance === bestDistance && v.capacity > best.capacity)) {
        best = v;
        bestDistance = distance;
      }
    });
    return best ? { variant: best, soldOut: false } : { variant: target, soldOut: true };
  }

  /* ---------- drawing the panels ---------- */

  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  function renderRoot(root, s) {
    var attrs = s.cart.attributes || {};
    var isGift = attrs['Gift'] === 'Yes';

    root.hidden = s.cart.item_count === 0;

    var tick = root.querySelector('[data-shrine-gift-tick]');
    var body = root.querySelector('[data-shrine-gift-body]');
    tick.checked = isGift;
    body.hidden = !isGift;

    // Message: never overwrite while the customer is typing or has unsaved typing.
    var ta = root.querySelector('[data-shrine-gift-message]');
    if (ta !== document.activeElement && !dirty.has(ta)) {
      var saved = attrs['Gift message'] || '';
      if (ta.value !== saved) ta.value = saved;
    }

    // Gift box row
    var d = defaultVariant(s);
    var row = root.querySelector('[data-shrine-gift-boxrow]');
    row.hidden = !d;
    if (d) {
      var sel = root.querySelector('[data-shrine-gift-size]');

      // The dropdown must list exactly the sizes the server says exist. The page normally draws them, but if it did not
      // (or they changed), build them here so the dropdown can never be empty.
      var rebuilt = false;
      var haveIds = Array.prototype.map.call(sel.options, function (o) { return o.value; }).join(',');
      var wantIds = s.variants.map(function (v) { return String(v.id); }).join(',');
      if (haveIds !== wantIds) {
        while (sel.firstChild) sel.removeChild(sel.firstChild);
        s.variants.forEach(function (v) {
          var opt = document.createElement('option');
          opt.value = String(v.id);
          sel.appendChild(opt);   // text and disabled are set just below
        });
        rebuilt = true;
      }

      // Sold-out sizes are shown but disabled (availability is fresh from the server on every draw).
      Array.prototype.forEach.call(sel.options, function (opt) {
        var v = variantById(s, parseInt(opt.value, 10));
        if (!v) return;
        opt.disabled = !v.available;
        var label = v.title + (v.available ? '' : ' \u2013 sold out');   // just the size, e.g. "2 bottles"
        if (opt.textContent !== label) opt.textContent = label;
      });

      // Go back to the suggested size when the bottle count changes (or the chosen size has sold out);
      // otherwise leave the customer's own pick alone.
      var chosen = variantById(s, parseInt(sel.value, 10));
      if (rebuilt || sel.dataset.bottles !== String(s.bottles) || !chosen || !chosen.available) {
        sel.value = String(d.variant.id);
        sel.dataset.bottles = String(s.bottles);
        chosen = d.variant;
      }

      // Line 1 follows the dropdown: "Add a wooden gift box (\u00a310)"
      setText(root.querySelector('[data-shrine-gift-boxtitle]'), 'Add a wooden gift box (' + chosen.price + ')');
      var imgWrap = root.querySelector('[data-shrine-gift-boximage]');
      imgWrap.hidden = !chosen.image;
      if (chosen.image) {
        var img = imgWrap.querySelector('img');
        if (img.getAttribute('src') !== chosen.image) {
          img.src = chosen.image;
          img.srcset = chosen.image2x ? chosen.image2x + ' 2x' : '';
        }
      }

      var btn = root.querySelector('[data-shrine-gift-box]');
      setText(btn, flash ? 'Added' : d.soldOut ? 'Sold out' : 'Add');
      btn.disabled = busy || flash || d.soldOut || !chosen.available;
    }

    var errEl = root.querySelector('[data-shrine-gift-error]');
    errEl.hidden = !errorText;
    setText(errEl, errorText);
  }

  function render(s) {
    last = s;
    document.querySelectorAll('[data-shrine-gift]').forEach(function (root) { renderRoot(root, s); });
  }

  /* ---------- syncing (reading and drawing only; never changes anything) ---------- */

  // Read the cart and draw. Calls made while one is already waiting are merged.
  function sync() {
    if (syncQueued) return Promise.resolve();
    syncQueued = true;
    return enqueue(function () {
      syncQueued = false;
      return fetchState().then(function (s) {
        errorText = '';   // a fresh, successful read of the cart: an old error is out of date
        render(s);
      });
    }).catch(function (err) {
      errorText = err && err.message && err.message.length > 3 ? err.message : GENERIC_ERROR;
      if (last) render(last);
    });
  }

  // Run a customer action through the queue, then redraw from the cart whether it worked or not.
  function act(fn, redrawBasket) {
    errorText = '';
    return enqueue(fn).then(function () {
      if (redrawBasket) document.dispatchEvent(new CustomEvent('cart:build'));   // redraw the basket lines
    }).catch(function (err) {
      errorText = (err && err.message) || GENERIC_ERROR;
    }).then(function () {
      return enqueue(function () { return fetchState().then(render); });
    }).catch(function () { if (last) render(last); });
  }

  /* ---------- customer actions ---------- */

  // Ticking sets Gift; unticking clears Gift and Gift message (an empty value removes an attribute). No box lines are touched.
  function onTick(root, tick) {
    var ta = root.querySelector('[data-shrine-gift-message]');
    root.querySelector('[data-shrine-gift-body]').hidden = !tick.checked;   // react straight away
    if (tick.checked) {
      return act(function () { return post('cart/update.js', { attributes: { Gift: 'Yes' } }); }, false);
    }
    window.clearTimeout(saveTimers.get(ta));
    dirty.delete(ta);
    return act(function () {
      return post('cart/update.js', { attributes: { 'Gift': '', 'Gift message': '' } });
    }, false);
  }

  // "Add": one box of the size picked in the dropdown, quantity 1. Then "Added" for a moment, so another can be added.
  function onAdd(root) {
    if (busy || flash) return;
    var sel = root.querySelector('[data-shrine-gift-size]');
    var picked = sel && sel.selectedOptions && sel.selectedOptions[0];
    var id = parseInt(sel && sel.value, 10);
    if (!id || !picked || picked.disabled) return;   // no valid size picked: never send a request
    busy = true;
    document.querySelectorAll('[data-shrine-gift-box]').forEach(function (b) { b.disabled = true; });   // straight away, before any request
    return act(function () {
      return post('cart/add.js', { items: [{ id: id, quantity: 1 }] });
    }, true).then(function () {
      busy = false;
      if (errorText) {
        if (last) render(last);
        return;
      }
      flash = true;
      if (last) render(last);
      announce('Gift box added');
      window.setTimeout(function () {
        flash = false;
        if (last) render(last);
      }, ADDED_FLASH_MS);
    });
  }

  // Say something through the polite live region.
  function announce(text) {
    document.querySelectorAll('[data-shrine-gift-live]').forEach(function (live) {
      live.textContent = '';
      window.setTimeout(function () { live.textContent = text; }, 50);   // empty first so a repeat is still read out
    });
  }

  function saveMessage(root, ta) {
    window.clearTimeout(saveTimers.get(ta));
    var text = ta.value;
    if (!text.trim()) text = '';   // an empty value removes the attribute
    var sent = ta.value;
    saving++;
    lastSave = act(function () {
      return post('cart/update.js', { attributes: { 'Gift message': text } });
    }, false).then(function () {
      saving--;
      // Saved and not edited since: the cart is now the truth. If it failed, keep the typing so it can be retried.
      if (!errorText && ta.value === sent) dirty.delete(ta);
    });
    return lastSave;
  }

  function onMessageInput(root, ta) {
    allowRetry = false;
    dirty.add(ta);
    window.clearTimeout(saveTimers.get(ta));
    saveTimers.set(ta, window.setTimeout(function () { saveMessage(root, ta); }, SAVE_DELAY_MS));
  }

  /* ---------- wiring ---------- */

  // One set of listeners on the document covers the drawer and the page, and survives any redraw.
  document.addEventListener('change', function (e) {
    var root = e.target.closest && e.target.closest('[data-shrine-gift]');
    if (!root) return;
    if (e.target.matches('[data-shrine-gift-tick]')) onTick(root, e.target);
    else if (e.target.matches('[data-shrine-gift-message]')) saveMessage(root, e.target);
    else if (e.target.matches('[data-shrine-gift-size]') && last) renderRoot(root, last);   // new size: new image and price
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-shrine-gift-box]');
    if (btn && !btn.disabled) onAdd(btn.closest('[data-shrine-gift]'));
  });

  document.addEventListener('input', function (e) {
    var root = e.target.closest && e.target.closest('[data-shrine-gift]');
    if (root && e.target.matches('[data-shrine-gift-message]')) onMessageInput(root, e.target);
  });

  /* ---------- checkout waits for the message to be saved ---------- */

  function hasUnsaved() {
    if (saving > 0) return true;
    return Array.prototype.some.call(document.querySelectorAll('[data-shrine-gift-message]'), function (ta) {
      return dirty.has(ta);
    });
  }

  // Save anything typed but not yet saved, then wait for the last save to finish.
  function flushUnsaved() {
    document.querySelectorAll('[data-shrine-gift-message]').forEach(function (ta) {
      if (dirty.has(ta)) saveMessage(ta.closest('[data-shrine-gift]'), ta);
    });
    return lastSave;
  }

  function resubmit(form, submitter) {
    if (form.requestSubmit) {
      try {
        form.requestSubmit(submitter || undefined);   // fires 'submit' again, so theme.js onSubmit (terms check) still runs
        return;
      } catch (e) { /* fall through */ }
    }
    if (submitter && submitter.name) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = submitter.name;
      hidden.value = submitter.value;
      form.appendChild(hidden);
    }
    passNext = null;
    form.submit();
  }

  // Capture phase on the document runs before theme.js's own submit handler on the form. If a gift message is
  // unsaved we hold the submit, wait for the save, then submit again for real. Express pay buttons never submit
  // this form; for them the save-on-blur (and keepalive) above is what protects the message.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.querySelector || !form.querySelector('[data-shrine-gift]')) return;
    if (passNext === form) { passNext = null; return; }
    if (!hasUnsaved()) return;
    if (allowRetry) { allowRetry = false; return; }

    e.preventDefault();
    e.stopPropagation();
    var submitter = e.submitter;
    if (submitter) submitter.classList.add('btn--loading');

    flushUnsaved().then(function () {
      if (errorText) {
        // Don't send them to checkout without their message; a second click goes through regardless.
        allowRetry = true;
        errorText = 'Your gift message couldn’t be saved. Press checkout again to continue without it.';
        if (last) render(last);
        if (submitter) submitter.classList.remove('btn--loading');
        return;
      }
      passNext = form;
      resubmit(form, submitter);
    });
  }, true);

  // Anything that changes the basket: quantity change, add to basket, opening the drawer, back button.
  // These only make the panel read the cart again (the bottle count, sold-out sizes); they never change anything.
  ['cart:updated', 'ajaxProduct:added', 'cart:open'].forEach(function (name) {
    document.addEventListener(name, function () {
      if (document.querySelector('[data-shrine-gift]')) sync();
    });
  });
  window.addEventListener('pageshow', function (e) {
    if (e.persisted && document.querySelector('[data-shrine-gift]')) sync();
  });

  function init() {
    var roots = document.querySelectorAll('[data-shrine-gift]');
    // An empty basket needs nothing: the first add to basket triggers a sync.
    if (roots.length && Array.prototype.some.call(roots, function (r) { return !r.hidden; })) sync();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
